import * as React from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'next-i18next';

import { GoogleAutoCompleteInput, GooglePlacePrediction } from '@restworld/ui-ds';
import { getLocationAddressFields } from '@restworld/utils-common';

import {
  ManualEntryPromptText,
  ManualEntryPromptLink,
  ManualEntryPromptWrapper,
  ManualEntryPromptHint,
} from './manual-venue-entry.styled';

export type ManualAddressResult = {
  addressString: string;
  lat?: number;
  lon?: number;
};

/**
 * "Your business not on Google Maps? Click here to enter manually" prompt.
 * Renders only when `visible` is true.
 */
export const ManualEntryPrompt: React.FC<{
  visible: boolean;
  onClick: () => void;
}> = ({ visible, onClick }) => {
  const { t } = useTranslation('restaurant-onboarding');

  if (!visible) return null;

  return (
    <ManualEntryPromptWrapper>
      <ManualEntryPromptText>
        {t('your_business_not_on_google_maps')}{' '}
        <ManualEntryPromptLink onClick={onClick}>{t('click_here_to_enter_manually')}</ManualEntryPromptLink>
      </ManualEntryPromptText>
      <ManualEntryPromptHint>{t('manual_entry_what_happens')}</ManualEntryPromptHint>
    </ManualEntryPromptWrapper>
  );
};

/**
 * Address autocomplete field with `['address']` filter that geocodes
 * the selected suggestion via `getLocationAddressFields`.
 *
 * Calls `onAddressChange` on every keystroke and `onAddressSelect`
 * with the geocoded result when a suggestion is picked.
 */
export const ManualAddressInput: React.FC<{
  formId: string;
  placeholder?: string;
  onAddressChange: (value: string) => void;
  onAddressSelect: (result: ManualAddressResult) => void;
}> = ({ formId, placeholder = 'es. Via Roma 1, 00100, Roma', onAddressChange, onAddressSelect }) => {
  const handleSuggestionSelect = React.useCallback(
    async (suggestion: GooglePlacePrediction) => {
      const { main_text, secondary_text } = suggestion.structured_formatting;
      const addressString = `${main_text}${secondary_text ? `, ${secondary_text}` : ''}`;
      onAddressChange(addressString);
      try {
        const addressFields = await getLocationAddressFields(addressString);
        onAddressSelect({
          addressString,
          lat: addressFields.address_lat,
          lon: addressFields.address_lon,
        });
      } catch {
        // Geocoding failed — pass address string without coordinates
        onAddressSelect({ addressString });
      }
    },
    [onAddressChange, onAddressSelect]
  );

  return (
    <GoogleAutoCompleteInput
      placesFilterTypes={['address']}
      formId={formId}
      key={formId}
      placeholder={placeholder}
      onSuggestionSelect={handleSuggestionSelect}
      inputProps={{
        bordercolor: 'neutral-90' as const,
        type: 'text',
        id: formId,
        isFullwidth: true,
        wrapperStyle: tw`w-full`,
        onChange: onAddressChange,
      }}
      dropdownItemProps={{
        itemRenderVariant: 'without_break_line' as const,
      }}
      mainWrapperStyle={tw`w-full`}
      withFloatingUI={true}
    />
  );
};
